import { CHAT_ROLE } from "@/types";
import { create } from "zustand";

interface EditingMessage {
  messageId: number;
  originalContent: string;
  isDeleted: boolean;
}

interface AttachmentPreview {
  attachmentList: { url: string; name?: string; type?: string }[];
  currentIndex: number;
}

type ChatState = {
  // state cho all chat functionality
  isConnectionReady: boolean;
  failConnectionMessage: string;

  // state for chat detail
  chatRole: CHAT_ROLE;
  memberNumber: number;
  // state for attachment lightbox
  attachments: AttachmentPreview | null;

  // State to edit/ delete message
  editingMessage?: EditingMessage;

  // Show/hide drawer for mobile view
  openLeftSection: boolean;
  setOpenLeftSection: (open: boolean) => void;

  // Unread count per room (key = roomId string)
  roomUnreadCounts: Record<string, number>;
  setRoomUnreadCountsFromApi: (counts: Record<string, number>) => void;
  resetRoomUnreadCount: (roomId: number | string) => void;
  incrementRoomUnreadCount: (roomId: number | string, amount?: number) => void;

  // methods
  setIsConnectionReady: (ready: boolean) => void;
  setChatRole: (role: CHAT_ROLE) => void;
  setFailConnectionMessage: (message: string) => void;
  setMemberNumber: (num: number) => void;
  setAttachment: (attachment: AttachmentPreview | null) => void;
  setEditingMessage: (message?: EditingMessage) => void;
};

const useChatStore = create<ChatState>((set) => ({
  // Data stored in the chat store
  isConnectionReady: false,
  chatRole: CHAT_ROLE.MEMBER,
  failConnectionMessage: "",
  memberNumber: 0,
  attachments: null,
  openLeftSection: false,
  roomUnreadCounts: {},

  // Methods to update the state
  setMemberNumber: (num) => set({ memberNumber: num }),
  setIsConnectionReady: (ready) => {
    if (ready) {
      // Clear fail message when connection is ready
      set({ failConnectionMessage: "", isConnectionReady: true });
      return;
    }
    set({ isConnectionReady: ready });
  },
  setChatRole: (role) => set({ chatRole: role }),
  setFailConnectionMessage: (message) =>
    set({ failConnectionMessage: message }),
  setAttachment: (attachments) => set({ attachments }),
  setEditingMessage: (message) => set({ editingMessage: message }),
  setOpenLeftSection: (open) => set({ openLeftSection: open }),
  setRoomUnreadCountsFromApi: (counts) => set({ roomUnreadCounts: counts }),
  resetRoomUnreadCount: (roomId) =>
    set((state) => ({
      roomUnreadCounts: {
        ...state.roomUnreadCounts,
        [roomId.toString()]: 0,
      },
    })),
  incrementRoomUnreadCount: (roomId, amount = 1) =>
    set((state) => {
      const roomKey = roomId.toString();
      return {
        roomUnreadCounts: {
          ...state.roomUnreadCounts,
          [roomKey]: (state.roomUnreadCounts[roomKey] || 0) + amount,
        },
      };
    }),
}));

export default useChatStore;
